import type { Session } from '@supabase/supabase-js'
import { type QueryKey, useQuery } from '@tanstack/react-query'
import { AUTH_BOOT_TIMEOUT_MS, rejectAfter } from '~/shared/lib/with-timeout'
import { useSupabaseSession } from './use-auth'

interface UseRoleAuthOptions<T> {
  queryKey: QueryKey
  queryFn: () => Promise<T>
  enabled: (session: Session | null | undefined) => boolean
  staleTime?: number
}

interface UseRoleAuthResult<T> {
  data: T | undefined
  error: unknown
  isError: boolean
  isLoading: boolean
  isAuthenticated: boolean
}

/** A failed background refetch keeps the last answer; only a failed first read signs out. */
export function roleAuthStatus(state: {
  sessionKnown: boolean
  enabled: boolean
  hasData: boolean
  isLoading: boolean
  isLoadingError: boolean
}): { isLoading: boolean; isAuthenticated: boolean } {
  return {
    isLoading: !state.sessionKnown || (state.enabled && state.isLoading),
    isAuthenticated: state.enabled && state.hasData && !state.isLoadingError,
  }
}

export function useRoleAuth<T>(opts: UseRoleAuthOptions<T>): UseRoleAuthResult<T> {
  const session = useSupabaseSession()
  const enabled = opts.enabled(session)

  const { data, isLoading, isError, isLoadingError, error } = useQuery({
    queryKey: opts.queryKey,
    // Bounded: a hung current-user read (down backend, dead session) resolves to
    // an error within the deadline instead of holding auth pages on a spinner.
    // The error path treats the user as logged out, so the app falls open to
    // /login rather than a blank screen.
    queryFn: () =>
      Promise.race([opts.queryFn(), rejectAfter(AUTH_BOOT_TIMEOUT_MS, 'auth read timed out')]),
    enabled,
    staleTime: opts.staleTime ?? 5 * 60 * 1000,
    retry: false,
  })

  return {
    data,
    error,
    isError,
    ...roleAuthStatus({
      sessionKnown: session !== undefined,
      enabled,
      hasData: data !== undefined,
      isLoading,
      isLoadingError,
    }),
  }
}
